import { BodyText, BASE_MARGIN, Avatar, PuzmoButton, ModalForm, Row } from "$components/DesignSystem"
import { CreateNewPlayerFormMutation } from "$relay/CreateNewPlayerFormMutation.graphql"
import React, { useContext, useState } from "react"
import { NativeSyntheticEvent, TextInputKeyPressEventData, View, Pressable, ScrollView } from "react-native"
import { graphql, useMutation } from "react-relay"
import { useTheme } from "styled-rn"

import { AuthModalContext } from "./AuthModal"
import { AppContext } from "../../../AppContext"
import { slugify } from "../../lib/slugify"
import { allAvatarPaths } from "../profileEditor/UserSettingsAvatarSelector"

export const CreateNewPlayerOnAccountForm = (props: object) => {
  const ctx = useContext(AppContext)
  const theme = useTheme()

  const modalCtx = useContext(AuthModalContext)

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const errored = !!errorMessage

  const [username, setUsername] = useState("")
  const [avatarIndex, setAvatarIndex] = useState(Math.round(Math.random() * allAvatarPaths.length) * 1 + 1)
  const [displayName, setDisplayName] = useState("")

  const onEnterSubmit = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === "Enter" && username && displayName) {
      onSubmit()
    }
  }

  const [createNew, loading] = useMutation<CreateNewPlayerFormMutation>(graphql`
    mutation CreateNewPlayerFormMutation($input: CreateNewUserOnAccountInput!) {
      createNewUserOnAccount(input: $input) {
        id
        username
        usernameID
      }
    }
  `)

  const onSubmit = () =>
    createNew({
      variables: {
        input: {
          username: username || slugify(displayName),
          displayName,
          avatarIndex,
        },
      },
      onCompleted: (data) => {
        if (data.createNewUserOnAccount) {
          ctx.apiClient.switchUser(data.createNewUserOnAccount.username, data.createNewUserOnAccount.usernameID)
          modalCtx.closeModal()
        } else {
          setErrorMessage("Could not create user")
          console.log("User not created")
        }
      },
    })

  return (
    <View style={{ position: "relative", padding: 36 }}>
      <ModalForm.Title>New player</ModalForm.Title>

      <Row>
        <View>
          <ModalForm.Subtitle>Display name</ModalForm.Subtitle>
          <ModalForm.TextInput
            key="displayName"
            value={displayName}
            onChangeText={setDisplayName}
            onKeyPress={onEnterSubmit}
            autoFocus={true}
            error={errored}
            textContentType="givenName"
            autoComplete="name"
            loading={loading}
            editable={!loading}
          />
          <ModalForm.Subtitle>Username</ModalForm.Subtitle>
          <Row>
            <ModalForm.TextInput
              pointerEvents="none"
              error={errored}
              editable={false}
              value="@"
              style={{ width: 28, color: theme.player }}
              focusable={false}
              loading={loading}
            />
            <ModalForm.TextInput
              key="username"
              style={{
                width: "100%",
              }}
              value={username || slugify(displayName)}
              onChangeText={(text) => setUsername(slugify(text))}
              onKeyPress={onEnterSubmit}
              error={errored}
              keyboardType="twitter"
              textContentType="username"
              loading={loading}
              editable={!loading}
            />
          </Row>
        </View>
        <View style={{ marginBottom: BASE_MARGIN, flex: 1, justifyContent: "center", alignContent: "center", alignItems: "center" }}>
          <Avatar source={{ uri: ctx.environment.CDNPath(`/avatars/${avatarIndex}.png`) }} size={146} />
        </View>
      </Row>

      <ScrollView style={{ maxHeight: 220 }}>
        <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
          {allAvatarPaths.map((avatar, i) => {
            return (
              <Pressable
                key={avatar}
                onPress={() => setAvatarIndex(i + 1)}
                accessibilityRole="button"
                accessibilityHint={`Select avatar ${i}`}
              >
                <View
                  style={{
                    width: 60,
                    height: 60,
                    marginRight: 10,
                    marginBottom: 10,
                    alignContent: "center",
                    justifyContent: "center",
                  }}
                >
                  <Avatar
                    source={{ uri: ctx.environment.CDNPath(avatar) }}
                    size={55}
                    style={{ borderColor: i === avatarIndex - 1 ? theme.player : "transparent" }}
                  />
                </View>
              </Pressable>
            )
          })}
        </View>
      </ScrollView>

      {errorMessage ? <BodyText style={{ color: theme.error, marginTop: 12 }}>{errorMessage}</BodyText> : null}

      <PuzmoButton
        block
        title={errored ? "Try again?" : "Create new player"}
        onPress={onSubmit}
        bgKey="a_puzmo"
        color="alwaysDark"
        style={{ marginTop: errored ? 12 : 24 }}
        disabled={!(displayName.length && (username || slugify(displayName)).length > 3)}
      />
    </View>
  )
}
